import { Button } from "../Button/index.js";
import { Dropdown } from "../Dropdown/index.js";
import { Tooltip } from "../Tooltip/index.js";
import React, { createElement, useLayoutEffect } from "react";
import { IconMenuHorizontalDots } from "@synthesis/shared-icons";
import { cn } from "@synthesis/shared-utils";
import { jsx, jsxs } from "react/jsx-runtime";
import { flushSync } from "react-dom";
//#region src/Toolbar/index.tsx
const GAP = 8;
const addResizeObserver = ({ root, onResize }) => {
	const resizeObserver = new ResizeObserver(onResize);
	resizeObserver.observe(root);
	return resizeObserver;
};
const ToolbarContext = React.createContext({});
const InnerToolbarContext = React.createContext({});
const ToolbarContextProvider = ({ children }) => {
	const [forwardedProps, setForwardedProps] = React.useState({});
	const [forwardedRefs, setForwardedRefs] = React.useState({});
	const [actions, setActions] = React.useState({
		action: null,
		option: null,
		forwardProps: (arg) => {
			setForwardedProps((prev) => ({
				...prev,
				...arg
			}));
		},
		forwardToolbarRefs: (refs) => {
			setForwardedRefs((prev) => ({
				...prev,
				...refs
			}));
		}
	});
	const handleSetActions = React.useCallback((nextActions) => setActions((prev) => ({
		...prev,
		...nextActions
	})), []);
	return /* @__PURE__ */ jsx(ToolbarContext.Provider, {
		value: actions,
		children: /* @__PURE__ */ jsx(InnerToolbarContext.Provider, {
			value: {
				handleSetActions,
				forwardedProps,
				forwardedRefs
			},
			children
		})
	});
};
const useToolbar = () => React.useContext(ToolbarContext);
const useInnerToolbar = () => React.useContext(InnerToolbarContext);
const arrToObj = (arr, value = true) => arr.reduce((acc, item) => {
	acc[item] = value;
	return acc;
}, {});
const ToolbarElement = React.memo(({ icon: IconComponent, asChild, children, className, disabled, action, label, tooltipDisabled, buttonLabel, theme = "text" }) => {
	if (asChild) {
		if (React.isValidElement(children)) {
			const childProps = { disabled };
			if (typeof action === "function") childProps.onClick = action;
			return React.cloneElement(children, childProps);
		}
		return children;
	}
	const Icon = IconComponent ? /* @__PURE__ */ jsx(IconComponent, {
		"data-cy": "toolbar-iconComponent",
		className: cn("size-5", disabled ? "text-neutrals-80" : "text-neutrals-60")
	}) : null;
	return /* @__PURE__ */ jsx(Tooltip, {
		description: label,
		arrowSide: "bottom",
		disabled: tooltipDisabled,
		children: /* @__PURE__ */ jsx("span", {
			className: "inline-flex",
			"aria-label": label,
			style: { cursor: disabled ? "not-allowed" : void 0 },
			children: /* @__PURE__ */ jsx(Button, {
				onClick: action,
				disabled,
				className,
				leftIcon: Icon,
				theme,
				size: "sm",
				children: buttonLabel ?? ""
			})
		})
	});
});
ToolbarElement.displayName = "ToolbarElement";
const DividerElement = ({ asChild, children, className, action: _action, disabled: _disabled, optionKey: _optionKey, ...rest }) => {
	if (asChild) return children;
	return /* @__PURE__ */ jsx("div", {
		className: cn("bg-neutrals-95 h-full w-px", className),
		...rest
	});
};
DividerElement.displayName = "DividerElement";
const DynamicElement = (_props) => {
	return null;
};
DynamicElement.displayName = "DynamicElement";
const isDividerElement = (element) => React.isValidElement(element) && element.type === DividerElement;
const useActionsAndOptions = () => {
	const { handleSetActions, forwardedProps, forwardedRefs } = useInnerToolbar();
	const [actions, setActions] = React.useState({});
	const [leftOptions, setLeftOptions] = React.useState([]);
	const [rightOptions, setRightOptions] = React.useState([]);
	const [disabledElements, setDisabledElements] = React.useState({});
	const leftOptionsRef = React.useRef(leftOptions);
	const rightOptionsRef = React.useRef(rightOptions);
	const actionsRef = React.useRef(actions);
	React.useLayoutEffect(() => {
		leftOptionsRef.current = leftOptions;
		rightOptionsRef.current = rightOptions;
		actionsRef.current = actions;
	}, [
		leftOptions,
		rightOptions,
		actions
	]);
	const getToolbarApi = React.useCallback(() => {
		const addOptions = (side, arg) => {
			const options = Array.isArray(arg) ? arg : [arg];
			if (side === "left") setLeftOptions((prev) => [...prev, ...options]);
			if (side === "right") setRightOptions((prev) => [...prev, ...options]);
			const defaultActions = options.reduce((acc, element) => {
				const { optionKey, defaultAction, action } = element.props;
				acc[optionKey] = defaultAction ?? action ?? (() => void 0);
				return acc;
			}, {});
			setActions((prev) => ({
				...prev,
				...defaultActions
			}));
		};
		const spliceOptions = (side, startIndex, deleteCount, ...options) => {
			const defaultActions = options.reduce((acc, element) => {
				const { optionKey, defaultAction, action } = element.props;
				acc[optionKey] = defaultAction ?? action ?? (() => void 0);
				return acc;
			}, {});
			const prevSplice = (prev) => {
				const prevCopy = [...prev];
				prevCopy.splice(startIndex, deleteCount, ...options);
				return prevCopy;
			};
			if (side === "left") setLeftOptions(prevSplice);
			if (side === "right") setRightOptions(prevSplice);
			setActions((prev) => ({
				...prev,
				...defaultActions
			}));
		};
		const removeOptions = (optionKeys) => {
			const optionsKeysObj = arrToObj(optionKeys);
			setLeftOptions((prev) => prev.filter(({ props }) => !optionsKeysObj[props.optionKey]));
			setRightOptions((prev) => prev.filter(({ props }) => !optionsKeysObj[props.optionKey]));
		};
		const cleanCustomOptions = (side) => {
			if (side === "left") setLeftOptions([]);
			if (side === "right") setRightOptions([]);
			if (side === "all") {
				setLeftOptions([]);
				setRightOptions([]);
			}
		};
		const disableOptions = (optionKeys) => {
			const optionsKeysObj = arrToObj(optionKeys);
			setDisabledElements((prev) => ({
				...prev,
				...optionsKeysObj
			}));
		};
		const enableOptions = (optionKeys) => {
			const optionsKeysObj = arrToObj(optionKeys, false);
			setDisabledElements((prev) => ({
				...prev,
				...optionsKeysObj
			}));
		};
		const replaceAllCustomOptions = (side, arg) => {
			const options = Array.isArray(arg) ? arg : [arg];
			if (side === "left") setLeftOptions(options);
			if (side === "right") setRightOptions(options);
		};
		const disableAllOptions = () => setDisabledElements(leftOptionsRef.current.reduce((acc, element) => {
			acc[element.props.optionKey] = true;
			return acc;
		}, {}));
		const enableAllOptions = () => setDisabledElements(leftOptionsRef.current.reduce((acc, element) => {
			acc[element.props.optionKey] = false;
			return acc;
		}, {}));
		const runAction = (type) => {
			try {
				actionsRef.current[type]?.();
			} catch {
				throw Error(`Не привязан action по ключу "${type}"`);
			}
		};
		const addActions = (actionsObj) => {
			setActions((prev) => ({
				...prev,
				...actionsObj
			}));
		};
		const removeActions = (optionKeys) => setActions((prev) => {
			const newValue = { ...prev };
			optionKeys.forEach((optionKey) => delete newValue[optionKey]);
			return newValue;
		});
		return {
			action: {
				run: runAction,
				add: addActions,
				remove: removeActions
			},
			option: {
				add: addOptions,
				remove: removeOptions,
				clean: cleanCustomOptions,
				disableMany: disableOptions,
				enableMany: enableOptions,
				replaceAll: replaceAllCustomOptions,
				disableAll: disableAllOptions,
				enableAll: enableAllOptions,
				splice: spliceOptions
			}
		};
	}, []);
	useLayoutEffect(() => {
		handleSetActions(getToolbarApi());
	}, [handleSetActions, getToolbarApi]);
	const cloneToolbarOption = React.useCallback((element) => {
		const { optionKey } = element.props;
		const baseProps = {
			disabled: disabledElements[optionKey],
			key: optionKey
		};
		const runAction = actions[optionKey];
		if (element.type === DynamicElement) {
			const { component: Component, ...dynamicProps } = element.props;
			const currentForwardedProps = forwardedProps[optionKey] || {};
			const currentForwardedRef = forwardedRefs[optionKey] || {};
			return /* @__PURE__ */ createElement(Component, {
				...dynamicProps,
				...baseProps,
				...runAction ? { action: runAction } : null,
				...currentForwardedProps,
				key: optionKey,
				ref: currentForwardedRef
			});
		}
		if (isDividerElement(element)) return React.cloneElement(element, baseProps);
		return React.cloneElement(element, {
			...baseProps,
			action: runAction
		});
	}, [
		actions,
		disabledElements,
		forwardedProps,
		forwardedRefs
	]);
	return {
		actions,
		disabledElements,
		leftOptions: leftOptions.map((element) => cloneToolbarOption(element)),
		rightOptions: rightOptions.map((element) => cloneToolbarOption(element)),
		forwardedProps
	};
};
const useResize = ({ leftOptions }) => {
	const containerRef = React.useRef(null);
	const elementsRef = React.useRef([]);
	const rightContainerRef = React.useRef(null);
	const toolbarRef = React.useRef(null);
	const dropdownButtonRef = React.useRef(null);
	const [index, setIndex] = React.useState(-1);
	const isCollapsed = index < leftOptions.length - 1;
	const addRef = React.useCallback((element) => {
		if (element === null) return;
		const elementIndex = Number(element.dataset.index);
		elementsRef.current[elementIndex] = element;
	}, []);
	const getEmptySpace = () => {
		const container = containerRef.current;
		const toolbar = toolbarRef.current;
		const rightContainer = rightContainerRef.current;
		const dropdownButton = dropdownButtonRef.current;
		if (!container || !toolbar || !rightContainer) return 0;
		return container.offsetWidth - toolbar.offsetWidth - rightContainer.offsetWidth - (isCollapsed && dropdownButton ? dropdownButton.offsetWidth : 0);
	};
	const onCompression = () => {
		setIndex((prev) => {
			let newIndex = prev;
			let emptySpace = getEmptySpace();
			const elementsWidths = elementsRef.current.map((elem) => elem?.offsetWidth ?? 0);
			while (newIndex >= -1) {
				const lastElementWidth = elementsWidths[newIndex] ?? 0;
				if (emptySpace <= 0) {
					newIndex--;
					emptySpace += lastElementWidth + GAP;
				} else return newIndex;
			}
			return prev;
		});
	};
	const onExpansion = () => {
		setIndex((prev) => {
			let newIndex = prev;
			let emptySpace = getEmptySpace();
			const elementsCount = elementsRef.current.length;
			const elementsWidths = elementsRef.current.map((elem) => elem?.offsetWidth ?? 0);
			if (prev < elementsCount - 1) {
				while (newIndex < elementsCount - 1) {
					const lastElementWidth = elementsWidths[newIndex + 1] ?? 0;
					if (lastElementWidth + GAP < emptySpace) {
						newIndex++;
						emptySpace -= lastElementWidth + GAP;
					} else return newIndex;
				}
				return newIndex;
			}
			return prev;
		});
	};
	const onResize = () => {
		const dropdownButton = dropdownButtonRef.current;
		if (!dropdownButton) return;
		const dropdownButtonWidth = dropdownButton.offsetWidth;
		const emptySpace = getEmptySpace();
		const needToExpand = emptySpace > dropdownButtonWidth + GAP && emptySpace > GAP;
		flushSync(needToExpand ? onExpansion : onCompression);
	};
	const onResizeRef = React.useRef(onResize);
	onResizeRef.current = onResize;
	React.useEffect(() => {
		elementsRef.current.length = leftOptions.length;
		const root = containerRef.current;
		if (!root) return;
		const resizeObserver = addResizeObserver({
			root,
			onResize: () => onResizeRef.current()
		});
		return () => {
			resizeObserver.disconnect();
		};
	}, [leftOptions]);
	return {
		index,
		containerRef,
		calculateWidthElement: /* @__PURE__ */ jsxs("div", {
			className: "pointer-events-none invisible absolute bottom-0 right-0 -z-50",
			children: [leftOptions.map((element, elementIndex) => /* @__PURE__ */ jsx("div", {
				ref: addRef,
				"data-index": elementIndex,
				className: "w-fit",
				children: element
			}, `${element.props?.optionKey ?? "element"}-${elementIndex}`)), /* @__PURE__ */ jsx(Button, {
				ref: dropdownButtonRef,
				leftIcon: /* @__PURE__ */ jsx(IconMenuHorizontalDots, { className: "size-5" })
			})]
		}),
		isCollapsed,
		rightContainerRef,
		toolbarRef
	};
};
const ToolbarRoot = React.memo(({ className, dropdownAutoClose = true }) => {
	const { disabledElements, actions, leftOptions, rightOptions } = useActionsAndOptions();
	const { index, containerRef, calculateWidthElement, isCollapsed, rightContainerRef, toolbarRef } = useResize({ leftOptions });
	const { toolbar, dropdown } = leftOptions.reduce((acc, element, i) => {
		if (i <= index) acc.toolbar.push(element);
		else acc.dropdown.push(element);
		return acc;
	}, {
		toolbar: [],
		dropdown: []
	});
	const dropdownScheme = React.useMemo(() => [{
		type: "ITEMS",
		elements: dropdown.filter((element) => !isDividerElement(element)).map((element) => {
			const { label, icon: IconComponent, value, optionKey } = element.props;
			const Icon = IconComponent ? /* @__PURE__ */ jsx(IconComponent, { className: "text-neutrals-60 size-5" }) : void 0;
			return {
				id: optionKey,
				label: /* @__PURE__ */ jsx("div", {
					className: "flex items-center justify-start gap-2",
					children: label || (typeof value === "function" ? value() : value)
				}),
				icon: Icon,
				disabled: disabledElements[optionKey]
			};
		})
	}], [dropdown, disabledElements]);
	const dropdownState = React.useMemo(() => {
		return dropdown.reduce((acc, element) => {
			const { optionKey } = element.props;
			const runAction = actions[optionKey];
			if (!isDividerElement(element) && runAction) acc[optionKey] = runAction;
			return acc;
		}, {});
	}, [dropdown, actions]);
	return /* @__PURE__ */ jsxs("div", {
		className: cn("border-neutrals-95 bg-neutrals-white relative flex w-full items-center justify-between overflow-clip border-b", className),
		ref: containerRef,
		children: [
			/* @__PURE__ */ jsxs("div", {
				className: "flex w-full items-center gap-2",
				children: [/* @__PURE__ */ jsx("div", {
					"data-cy": "toolbar-div-left",
					className: "flex h-14 shrink-0 items-center gap-2 px-2",
					ref: toolbarRef,
					children: toolbar
				}), isCollapsed && /* @__PURE__ */ jsx(Dropdown, {
					size: "sm",
					autoClose: dropdownAutoClose,
					scheme: dropdownScheme,
					state: dropdownState,
					trigger: /* @__PURE__ */ jsx(Button, {
						className: "mr-auto",
						leftIcon: /* @__PURE__ */ jsx(IconMenuHorizontalDots, {
							"data-cy": "toolbar-iconMenuHorizontalDots",
							className: "size-5"
						}),
						theme: "text",
						size: "sm"
					})
				})]
			}),
			/* @__PURE__ */ jsx("div", {
				ref: rightContainerRef,
				"data-cy": "toolbar-div-right",
				className: "flex h-[3.5625rem] w-fit shrink-0 items-center gap-2 px-2",
				children: rightOptions
			}),
			calculateWidthElement
		]
	});
});
ToolbarRoot.displayName = "ToolbarRoot";
const Toolbar = {
	Root: ToolbarRoot,
	Element: ToolbarElement,
	Divider: DividerElement,
	DynamicElement
};
//#endregion
export { Toolbar, ToolbarContextProvider, useToolbar };

//# sourceMappingURL=index.js.map